import type {
    DirectLink,
    DirectLinkSummary,
    Participant,
    Project,
    ProjectSurface,
} from "../types/PersologOnlineTypes";
import { toBackendDateTime } from "@persolog/utils";

/** A project surface widened to the full Project shape so it can drive the inline overview card. */
export type ProjectListItem = Omit<Project, "id"> & {
    id: number;
    participantCount: number;
    directLink: DirectLinkSummary | null;
};

/** Maps a lightweight surface to the full Project shape the overview card components expect. */
export function surfaceToListItem(surface: ProjectSurface): ProjectListItem {
    return {
        id: surface.id,
        name: surface.name,
        product: surface.product,
        defaultProduct: surface.defaultProduct,
        language: surface.language,
        status: surface.status,
        recipients: surface.recipients,
        additionalRecipient: surface.additionalRecipient,
        focus: surface.focus,
        anonymous: surface.anonymous,
        pseudonymMode: surface.pseudonymMode,
        creation: surface.creation,
        editedDate: surface.editedDate,
        archiveDate: surface.archiveDate ?? undefined,
        deletionDate: surface.deletionDate,
        // Core fields that are only loaded when a project is opened — defaulted for the list view.
        description: "",
        participantRetentionPeriod: "",
        participantDeletionNotification: true,
        participantCount: surface.participantCount,
        directLink: surface.directLink ?? null,
    };
}

/**
 * Builds the per-project direct-link map from each list item's embedded surface summary — no
 * separate fetch. Items without a direct link are omitted.
 */
export function buildDirectLinkMap(projects: ProjectListItem[]): Record<number, DirectLink> {
    const map: Record<number, DirectLink> = {};
    for (const item of projects) {
        if (item.directLink) {
            map[item.id] = {
                id: item.directLink.id,
                project: item,
                quantity: item.directLink.quantity,
                hash: item.directLink.hash,
                shortUrl: item.directLink.shortUrl,
                shortLinkExpiresAt: item.directLink.shortLinkExpiresAt,
                expiresAt: item.directLink.expiresAt,
            };
        }
    }
    return map;
}

/**
 * Checks if the given participants contain a participant whose id matches participantId. Returns
 * false when either input is missing.
 */
export function hasParticipant(
    participants: Participant[] | undefined,
    participantId: string | undefined,
): boolean {
    if (participants && participantId) {
        return participants.some((participant) => participant.id === Number(participantId));
    }
    return false;
}

/**
 * Formats a date-range bound as backend-friendly `yyyy-MM-dd HH:mm`, snapping to the start (00:00)
 * or end (23:59) of the locally picked day. Creation/edited dates are stored as UTC wall-clock
 * strings, so the local day boundary is converted to UTC before the backend compares them.
 */
export function formatDateBound(date: Date, endOfDay: boolean): string {
    const bound = new Date(date);
    if (endOfDay) {
        bound.setHours(23, 59, 59, 999);
    } else {
        bound.setHours(0, 0, 0, 0);
    }
    return toBackendDateTime(bound).slice(0, 16);
}

/** Returns the translated product name, falling back to the raw product key when none is known. */
export function getTranslatedProductName(
    productTranslations: Record<string, string>,
    product: string,
): string {
    return productTranslations[product.toLowerCase()] || product;
}
